import React, { Component, Fragment } from 'react';
import Grid from '../../../component/form/Grid';
import withStyles from '@mui/styles/withStyles';
import { Query } from '../../../reactApolloCompat';
import gql from 'graphql-tag';
import { getMyClient } from '../../../apollo';
import LinearProgress from '@mui/material/LinearProgress';
import Table, {
    TableCell,
    TableHeaderCell,
    TableNestedRow,
    TableSecondaryHeaderRow
} from '../../../component/form/Table';
import { shortDateTimeFromString } from '../../../util/date';
import { joinDefined } from '../../../util/strings';
import { CompletedIcon, ExclamationTriangleIcon, QuestionMarkCircleIcon } from '../../../component/IconIndex';
import { OutlineButton } from '../../../component/form/PrimaryButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import Hidden from '../../../component/Hidden';
import CloseIcon from '../../../component/icon/CloseIcon';
import DialogActions from '@mui/material/DialogActions';
import { DialogContent } from '@mui/material';

class TransferlogTable extends Component {
    state = {
        open: false
    };

    onCancel = (event, reason) => {
        if (reason === 'backdropClick') {
            return;
        }
        this.setState({ open: false });
    };

    render() {
        const { open } = this.state;
        const { legacyKey, funeralID, classes, useModal } = this.props;
        if (!(funeralID > 0)) return null;
        if (!useModal) {
            return this.renderTable();
        }
        return (
            <Fragment>
                <OutlineButton text="View Transfer Actions..." onClick={() => this.setState({ open: true })} />
                {!!open && (
                    <Dialog open={!!open} className={classes.root} onClose={this.onCancel}>
                        <DialogTitle className={classes.modalTitle}>Transfer Actions: Funeral {legacyKey}</DialogTitle>
                        <DialogContent className={classes.modalContent}>{this.renderTable()}</DialogContent>
                        <DialogActions className={classes.modalFooter}>
                            <div style={{ flexGrow: 2 }} />
                            <OutlineButton onClick={() => this.onCancel()} color="primary">
                                <CloseIcon />
                                <Hidden mdDown>Close</Hidden>
                            </OutlineButton>
                        </DialogActions>
                    </Dialog>
                )}
            </Fragment>
        );
    }

    renderTable() {
        const { funeralID } = this.props;
        if (!(funeralID > 0)) return null;
        return (
            <Query
                client={getMyClient()}
                query={queryChangeLog}
                variables={{ funeralID: Number(funeralID), limit: 25, offset: 0 }}
                fetchPolicy="network-only"
                notifyOnNetworkStatusChange
            >
                {results => this.renderForm(results)}
            </Query>
        );
    }

    renderForm = ({ error, loading, data }) => {
        const { classes } = this.props;
        const { readTransferActions: readDataChangeRecords } = data || {};
        return (
            <Fragment>
                <Grid container>
                    <div className={classes.loading}>
                        {(!!loading && (
                            <>
                                <LinearProgress /> Loading...
                            </>
                        )) ||
                            (!!error && `Error! ${error}`)}
                    </div>
                    <Grid item xs={12}>
                        {!!readDataChangeRecords &&
                        readDataChangeRecords.edges &&
                        readDataChangeRecords.edges.length ? (
                            <Table className="no-responsive" columns={['Transfer', 'Precautions', 'Actioned']}>
                                {readDataChangeRecords.edges.map((e, i) => this.renderEntry(e.node, i))}
                            </Table>
                        ) : (
                            <p>There is no log of transfers.</p>
                        )}
                    </Grid>
                </Grid>
            </Fragment>
        );
    };

    renderEntry = (entry, idx) => {
        const { classes } = this.props;
        const staffList = joinDefined(
            entry.AssignedStaff.edges?.map(({ node }) => joinDefined([node.FirstName, node.Surname], ' ')),
            ', '
        );
        const colSpan = 3;
        return (
            <Fragment key={entry.ID}>
                <TableSecondaryHeaderRow>
                    <TableHeaderCell>{!idx ? 'Inbound Transfer' : 'Transfer #' + (1 + idx)}</TableHeaderCell>
                    <TableHeaderCell tiny centred>
                        <div className={classes.warn}>
                            {entry.Bariatric && (
                                <div>
                                    <ExclamationTriangleIcon />
                                    <br />
                                    Bariatric
                                </div>
                            )}
                            {entry.InfectionRisk && (
                                <div>
                                    <ExclamationTriangleIcon />
                                    <br />
                                    Infectious
                                </div>
                            )}
                            {entry.Decomposed && (
                                <div>
                                    <ExclamationTriangleIcon />
                                    <br />
                                    Decomposed
                                </div>
                            )}
                        </div>
                    </TableHeaderCell>
                    <TableHeaderCell tiny centred>
                        {entry.TransferActioned ? <CompletedIcon /> : <QuestionMarkCircleIcon />}
                    </TableHeaderCell>
                </TableSecondaryHeaderRow>
                <TableNestedRow>
                    <TableCell colSpan={colSpan}>
                        <div>
                            <strong>Pickup: </strong>
                            {entry.DepartureText}
                        </div>
                    </TableCell>
                </TableNestedRow>
                <TableNestedRow>
                    <TableCell colSpan={colSpan}>
                        <div>
                            <strong>Destination: </strong>
                            {entry.DestinationText}
                        </div>
                    </TableCell>
                </TableNestedRow>
                <TableNestedRow>
                    <TableCell colSpan={colSpan}>
                        <div>
                            <strong>Staff: </strong>
                            {staffList}
                        </div>
                        {entry.Notes && (
                            <div>
                                <i>
                                    <small className={classes.comments}>{entry.Notes}</small>
                                </i>
                            </div>
                        )}
                    </TableCell>
                </TableNestedRow>
                {entry.TransferActioned && (
                    <TableNestedRow>
                        <TableCell colSpan={colSpan}>
                            <div>
                                <strong>When: </strong>
                                {shortDateTimeFromString(joinDefined([entry.CompletedAt, entry.CompletedAtTime], ' '))}
                                {entry.CompletedBy?.ID > 0 &&
                                    ' by ' + joinDefined([entry.CompletedBy.FirstName, entry.CompletedBy.Surname], ' ')}
                            </div>
                        </TableCell>
                    </TableNestedRow>
                )}
            </Fragment>
        );
    };
}

const queryChangeLog = gql`
    query ReadTransferLog($funeralID: ID!, $limit: Int, $offset: Int) {
        readTransferActions(filter: { FuneralID: { eq: $funeralID } }, limit: $limit, offset: $offset) {
            edges {
                node {
                    ID
                    Created
                    LastEdited
                    Notes
                    Bariatric
                    InfectionRisk
                    CompletedAt
                    CompletedAtTime
                    TransferActioned
                    DepartureText
                    DestinationText
                    Destination {
                        ID
                        Name
                        Phone
                        FullAddress
                    }
                    AssignedStaff {
                        edges {
                            node {
                                ID
                                FirstName
                                Surname
                            }
                        }
                    }
                    CreatedBy {
                        ID
                        FirstName
                        Surname
                    }
                    CompletedBy {
                        ID
                        FirstName
                        Surname
                    }
                }
            }
            pageInfo {
                hasNextPage
            }
        }
    }
`;

const styles = theme => ({
    root: {
        '& > div:nth-child(2) > div': {
            maxWidth: 660,
            width: '98vw',
            height: 800,
            maxHeight: '94vh',
            [theme.breakpoints.down('lg')]: {
                margin: 16
            }
        }
    },
    modalTitle: {
        padding: 24,
        margin: 0,
        background: theme.palette.contentForeground[theme.funeralHome],
        color: '#FFFFFF',
        fontWeight: 'lighter',
        [theme.breakpoints.up('md')]: {
            fontSize: 26
        }
    },
    modalFooter: {
        padding: '10px 20px 20px',
        margin: 0
    },
    modalContent: {
        position: 'relative',
        paddingTop: `${theme.spacing(2)}!important`,
        height: 'auto',
        overflowY: 'auto',
        overflowX: 'auto',
        '& p': {
            color: theme.palette.contentForeground[theme.funeralHome]
        },
        [theme.breakpoints.up('md')]: {
            padding: '16px 48px 24px 48px'
        }
    },
    comments: {
        wordBreak: 'break-word'
    },
    warn: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        '& > div': {
            lineHeight: 0.5,
            textAlign: 'center',
            color: theme.palette.validation.suggested,
            marginTop: -6,
            marginBottom: -6
        }
    },
    loading: { width: '100%', position: 'sticky', top: 0 },
    loadMore: { textAlign: 'center', margin: '12px auto 6px' }
});

export default withStyles(styles)(TransferlogTable);
